import {
  DEFAULT_THEME,
  type PresetKey,
  PRESETS,
  type Theme,
  type ThemeColors,
} from './themes'

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

const VALID_PRESET_KEYS: PresetKey[] = [...PRESETS.map((p) => p.key), 'custom']

function isColors(value: unknown): value is ThemeColors {
  if (typeof value !== 'object' || value === null) return false
  const c = value as Record<string, unknown>
  return (['lane1', 'lane2', 'you', 'opp'] as const).every(
    (k) => typeof c[k] === 'string' && HEX.test(c[k] as string),
  )
}

export function resolveTheme(stored: string | null): Theme {
  if (!stored) return DEFAULT_THEME
  try {
    const parsed = JSON.parse(stored) as unknown
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_THEME
    const { presetKey, colors } = parsed as Record<string, unknown>
    if (!VALID_PRESET_KEYS.includes(presetKey as PresetKey))
      return DEFAULT_THEME
    if (!isColors(colors)) return DEFAULT_THEME
    return { presetKey: presetKey as PresetKey, colors }
  } catch {
    return DEFAULT_THEME
  }
}

// Expand #rgb to #rrggbb, return [r, g, b] as 0-255.
function toRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '')
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const n = parseInt(h, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Pick black or white for legible text on the given background color,
// using the WCAG relative-luminance threshold. 0.179 is the crossover
// luminance (sqrt(1.05 * 0.05) - 0.05) where black and white text give
// equal contrast; brighter backgrounds get black text.
export function textColorFor(hex: string): '#000' | '#fff' {
  const [r, g, b] = toRgb(hex).map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.179 ? '#000' : '#fff'
}

// Apply the four base colors + derived text-contrast vars onto a root element.
export function applyThemeVars(
  colors: ThemeColors,
  root: HTMLElement = document.documentElement,
): void {
  root.style.setProperty('--lane-1', colors.lane1)
  root.style.setProperty('--lane-2', colors.lane2)
  root.style.setProperty('--checker-you', colors.you)
  root.style.setProperty('--checker-opp', colors.opp)
  root.style.setProperty('--checker-you-text', textColorFor(colors.you))
  root.style.setProperty('--checker-opp-text', textColorFor(colors.opp))
}
